import { Box, Flex, IconButton, Text } from '@chakra-ui/react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import React, { memo, useCallback, useRef } from 'react';

import { useColorModeValue } from '@components/ui/color-mode';
import NavigationEntityIcon from '@features/navigation/NavigationEntityIcon';
import { useEntityToggleState } from '@features/navigation/ToggleStateProvider';
import { preventEventDefault, stopEventPropagation } from 'helpers/browserEvent';
import {
  isSelectedNavigationEntity,
  NavigationEntity,
  useIsSelected,
  useSidebarNavigationEntities,
} from 'hooks/useNavigationEntities';
import RawArrowIcon from 'vectors/Arrowhead';

interface Props {
  entity: NavigationEntity;
  depth?: number;
  onSelect?: (entity: NavigationEntity) => void;
  selectedNavigationEntity?: NavigationEntity;
}

const NAVIGATION_ITEM_HEIGHT = '30px';
export const NavigationItem: React.FC<Props> = React.memo(
  ({ entity, depth = 0, onSelect, selectedNavigationEntity }) => {
    const isSelectedPath = useIsSelected(entity);
    const [isToggled, toggle] = useEntityToggleState(entity);
    const isDark = useColorModeValue(false, true);
    const router = useRouter();
    const pathname = usePathname();

    const isSelected =
      isSelectedPath ||
      (!isToggled &&
        entity.children.some((child) =>
          isSelectedNavigationEntity({ entity: child, selectedNavigationEntity }),
        ));
    if (entity.id === 'procurements') {
      console.log({
        isSelectedPath,
        isSelected,
      });
    }

    const onClick = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (entity.absolutePath != null) {
          if (!isToggled) {
            toggle();
          }
          onSelect?.(entity);
        }
        if (pathname === entity.absolutePath) {
          preventEventDefault(e);
        }
      },
      [entity, toggle, router, pathname, isToggled, onSelect],
    );

    const onClickToggle = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        toggle();
        preventEventDefault(e);
      },
      [toggle],
    );

    const { byId: entitiesById } = useSidebarNavigationEntities();
    const itemRef = useRef<HTMLDivElement>(null);

    let background: string | undefined;
    if (isSelected) {
      background = 'sidebar.hover';
    }

    const indentationPaddingLeft = `${depth * 8}px`;
    return (
      <>
        <Box data-testid="nav-item" position="relative">
          <Link href={entity.absolutePath ?? ''} prefetch>
            <Flex
              ref={itemRef}
              data-testid={`nav-${entity.name}`}
              tabIndex={-1}
              role="navigation"
              alignItems="center"
              marginX={1}
              borderRadius="md"
              cursor="pointer"
              height={NAVIGATION_ITEM_HEIGHT}
              transition="background .2s"
              bgColor={background}
              color={isDark ? (isSelected ? 'surface.background' : 'text.primary') : undefined}
              _hover={
                isDark ? { bgColor: 'sidebar.hover', color: 'white' } : { bgColor: 'sidebar.hover' }
              }
              onClick={onClick}
            >
              {depth > 0 && <div style={{ paddingLeft: indentationPaddingLeft }} />}
              <Flex flex="1" gap={3} overflow="hidden">
                <EmptySpace depth={depth} />
                <Flex alignItems="center" justifyContent="center" minHeight={7} minWidth={7}>
                  <NavigationEntityIcon entity={entity} />
                </Flex>
                <Flex
                  flex="1"
                  fontSize="sm"
                  fontWeight="medium"
                  alignItems="center"
                  color="text.primary"
                >
                  <Text wordBreak="break-all" flexGrow={1}>
                    {entity.name}
                  </Text>
                  {entity.children.length > 0 && (
                    <ArrowIcon open={isToggled} onClick={onClickToggle} />
                  )}
                </Flex>
              </Flex>
            </Flex>
          </Link>
        </Box>
        {isToggled && (
          <>
            {entity.children.map((child) => {
              const childEntity = entitiesById[child.id];
              if (childEntity == null) {
                return null;
              }
              return (
                <NavigationItem
                  key={`${childEntity.id}`}
                  entity={childEntity}
                  depth={depth + 1}
                  onSelect={onSelect}
                />
              );
            })}
          </>
        )}
      </>
    );
  },
);

NavigationItem.displayName = 'NavigationItem';

const EmptySpace = React.memo(({ depth }: { depth: number }) => {
  return <div style={{ paddingLeft: depth === 0 ? 0 : '4px' }} />;
});

const ArrowIcon = memo(
  ({ open, onClick }: { open: boolean; onClick: React.MouseEventHandler<HTMLButtonElement> }) => {
    const onClickHandler = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        stopEventPropagation(event);
        onClick(event);
      },
      [onClick],
    );
    return (
      <IconButton
        variant="ghost"
        size="2xs"
        color="text.primary"
        borderRadius="md"
        mr={2}
        cursor="pointer"
        onClick={onClickHandler}
      >
        <RawArrowIcon boxSize="8px" direction={open ? 'down' : 'right'} />
      </IconButton>
    );
  },
);

EmptySpace.displayName = 'EmptySpace';
